import {
  NOTIFICATION_KIND_LABELS,
  formatDateTime,
  notificationListSchema,
  notificationPreferencesSchema,
  notificationReadAllSchema,
  notificationSchema,
  type Notification,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, CheckCheck, Inbox, MailCheck } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { notificationsKey } from '@/features/notifications/NotificationBell';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const PAGE_SIZE = 20;
const preferencesKey = [...notificationsKey, 'preferences'] as const;

/**
 * Central de notificações (T-113d, RF-42; DEC-048): menções e avisos da pessoa, lidas e não lidas, com o link
 * para o assunto; marcar uma ou todas como lidas; preferência de e-mail das menções.
 */
export function NotificacoesPage() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [page, setPage] = useState(1);
  const list = useQuery({
    queryKey: [...notificationsKey, 'list', { onlyUnread, page }],
    queryFn: ({ signal }) =>
      client.request(
        `/me/notifications?page=${page}&pageSize=${PAGE_SIZE}${onlyUnread ? '&unread=true' : ''}`,
        { schema: notificationListSchema, signal },
      ),
    placeholderData: keepPreviousData,
  });
  // Página que deixou de existir (ex.: todas lidas com o filtro "não lidas") volta para a última.
  const totalPages = list.data?.totalPages;
  useEffect(() => {
    if (totalPages === undefined) return;
    const last = Math.max(1, totalPages);
    if (page > last) setPage(last);
  }, [page, totalPages]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: notificationsKey });

  const mark = useMutation({
    meta: { handlesError: true },
    mutationFn: ({ id, read }: { id: string; read: boolean }) =>
      client.request(`/me/notifications/${id}/${read ? 'read' : 'unread'}`, {
        method: 'POST',
        schema: notificationSchema,
      }),
    onSuccess: refresh,
    onError: (err) =>
      toast.error('Não foi possível marcar a notificação', { description: errorMessage(err) }),
  });
  const markAll = useMutation({
    meta: { handlesError: true },
    mutationFn: () =>
      client.request('/me/notifications/read-all', {
        method: 'POST',
        schema: notificationReadAllSchema,
      }),
    onSuccess: async (res) => {
      toast.success(
        res.marked === 1
          ? '1 notificação marcada como lida'
          : `${res.marked} notificações marcadas como lidas`,
      );
      await refresh();
    },
    onError: (err) => toast.error('Não foi possível marcar todas', { description: errorMessage(err) }),
  });

  const unread = list.data?.unread ?? 0;
  return (
    <PageContainer className="max-w-4xl">
      <PageHeader
        title="Notificações"
        description="Menções em comentários, decisões sobre as metas que você propôs e alertas de que você é responsável, cada um com o link para o assunto."
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={!unread || markAll.isPending}
            loading={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            <CheckCheck aria-hidden />
            Marcar todas como lidas
          </Button>
        }
      />
      <EmailPreference className="mt-6" />
      <div
        className="mt-6 flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Quais notificações mostrar"
      >
        {(
          [
            [false, 'Todas'],
            [true, unread ? `Não lidas (${unread})` : 'Não lidas'],
          ] as const
        ).map(([value, label]) => (
          <Button
            key={label}
            size="sm"
            variant={onlyUnread === value ? 'primary' : 'outline'}
            aria-pressed={onlyUnread === value}
            onClick={() => {
              setOnlyUnread(value);
              setPage(1);
            }}
          >
            {label}
          </Button>
        ))}
      </div>
      <div className="mt-4">
        {list.isPending ? (
          <LoadingState rows={4} label="Carregando notificações" />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
        ) : list.data.items.length === 0 ? (
          onlyUnread ? (
            <EmptyState
              icon={MailCheck}
              title="Tudo lido"
              description="Você não tem notificações não lidas. As anteriores continuam em Todas."
            />
          ) : (
            <EmptyState
              icon={Inbox}
              title="Nenhuma notificação ainda"
              description="Quando alguém mencionar você com @, decidir sobre metas que você propôs ou surgir um alerta de que você é responsável, o aviso aparece aqui."
            />
          )
        ) : (
          <>
            <ol
              aria-label="Notificações"
              className="flex flex-col divide-y divide-border rounded-md border border-border"
            >
              {list.data.items.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  busy={mark.isPending && mark.variables?.id === n.id}
                  onOpen={() => {
                    if (!n.readAt) mark.mutate({ id: n.id, read: true });
                  }}
                  onToggle={() => mark.mutate({ id: n.id, read: !n.readAt })}
                />
              ))}
            </ol>
            {list.data.totalPages > 1 ? (
              <nav
                aria-label="Paginação de notificações"
                className="mt-4 flex items-center justify-between text-sm"
              >
                <span className="text-muted-foreground">
                  Página {list.data.page} de {list.data.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((x) => x - 1)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= list.data.totalPages}
                    onClick={() => setPage((x) => x + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </nav>
            ) : null}
          </>
        )}
      </div>
    </PageContainer>
  );
}

function NotificationRow({
  notification: n,
  busy,
  onOpen,
  onToggle,
}: {
  notification: Notification;
  busy: boolean;
  onOpen: () => void;
  onToggle: () => void;
}) {
  const unread = !n.readAt;
  return (
    <li className={cn('px-4 py-3', unread && 'bg-brand-soft/40')}>
      <article aria-label={`${unread ? 'Não lida: ' : ''}${n.title}`} className="flex gap-3">
        <span
          aria-hidden
          className={cn('mt-1.5 size-2.5 shrink-0 rounded-full', unread ? 'bg-brand' : 'bg-transparent')}
        />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
            <span className="font-semibold uppercase">{NOTIFICATION_KIND_LABELS[n.kind]}</span>
            {n.important ? <StatusBadge status="red" label="Importante" /> : null}
            <time dateTime={n.createdAt}>{formatDateTime(n.createdAt)}</time>
            <span className="sr-only">{unread ? 'Não lida' : 'Lida'}</span>
          </p>
          <h2 className={cn('mt-0.5 text-sm break-words', unread ? 'font-bold' : 'font-medium')}>
            {n.title}
          </h2>
          {n.body ? (
            <p className="mt-1 line-clamp-3 text-sm break-words whitespace-pre-line text-muted-foreground">
              {n.body}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to={n.link} onClick={onOpen} aria-label={`Abrir: ${n.title}`}>
                Abrir
              </Link>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={onToggle}
              aria-label={`${unread ? 'Marcar como lida' : 'Marcar como não lida'}: ${n.title}`}
            >
              {unread ? 'Marcar como lida' : 'Marcar como não lida'}
            </Button>
          </div>
        </div>
      </article>
    </li>
  );
}

/** "E-mail quando me mencionarem" (padrão ligado), gravado na hora. */
function EmailPreference({ className }: { className?: string }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const id = useId();
  // Escolha mostrada na hora do clique (a gravação vem depois; se falhar, volta ao valor salvo).
  const [choice, setChoice] = useState<boolean | null>(null);
  const prefs = useQuery({
    queryKey: preferencesKey,
    queryFn: ({ signal }) =>
      client.request('/me/notification-preferences', { schema: notificationPreferencesSchema, signal }),
  });
  const save = useMutation({
    meta: { handlesError: true },
    mutationFn: (emailMentions: boolean) =>
      client.request('/me/notification-preferences', {
        method: 'PUT',
        body: { emailMentions },
        schema: notificationPreferencesSchema,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(preferencesKey, saved);
      toast.success(
        saved.emailMentions ? 'Você vai receber e-mail das menções' : 'E-mail das menções desligado',
      );
    },
    onError: (err) =>
      toast.error('Não foi possível salvar a preferência', { description: errorMessage(err) }),
    onSettled: () => setChoice(null),
  });
  return (
    <section
      aria-label="Preferências de notificação"
      className={cn('rounded-md border border-border p-4', className)}
    >
      {prefs.isPending ? (
        <LoadingState rows={1} label="Carregando preferências" />
      ) : prefs.isError ? (
        <ErrorState
          title="Não foi possível carregar a preferência de e-mail"
          error={prefs.error}
          onRetry={() => void prefs.refetch()}
          retrying={prefs.isFetching}
        />
      ) : (
        <div className="flex items-start gap-3 text-sm">
          <input
            id={id}
            type="checkbox"
            aria-describedby={`${id}-dica`}
            className="mt-0.5 size-4 shrink-0 accent-(--brand)"
            checked={choice ?? prefs.data.emailMentions}
            disabled={save.isPending}
            onChange={(e) => {
              setChoice(e.target.checked);
              save.mutate(e.target.checked);
            }}
          />
          <div>
            <label htmlFor={id} className="font-semibold">
              Receber e-mail quando alguém me mencionar
            </label>
            <p id={`${id}-dica`} className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              {prefs.data.emailMentions ? null : <BellOff className="size-3.5" aria-hidden />}
              {prefs.data.emailMentions
                ? 'O e-mail traz o comentário e o link para o assunto. A notificação aqui chega sempre.'
                : 'Desligado: as menções continuam chegando aqui, só não vão por e-mail.'}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
